import { Avatar, AvatarFallback, Skeleton } from '@folio/components';
import { cn, formatDate, initialsOf } from '@folio/lib';
import { useQuery } from '@tanstack/react-query';
import type { EmailThreadEmailDto, EmailThreadLinkedPolicyDto } from 'korint-sdk';
import { uniqBy } from 'lodash';
import { emailThreadControllerGetThreadQueryOptions } from 'korint-sdk';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmailDetail } from '../EmailDetailModule/EmailDetailModule';
import { ArchiveButton } from './ArchiveButton';
import { EmailPolicyLinks } from './EmailPolicyLinks';
import { InboxDate } from './InboxDate';
import { counterpartOf, participantAddresses } from './threadHelpers';
import { ToAttachBadge } from './ThreadIndicators';
import { Stack } from '@folio/components/ui/flex';
import { Text, Title } from '@folio/components/ui/text';

function ThreadEmailSection({
  threadId,
  email,
  threadSubject,
  threadPolicies,
  participants,
  policyId,
  isOpen,
  onToggle,
  onSetArchived,
}: {
  threadId: string;
  email: EmailThreadEmailDto;
  threadSubject: string;
  threadPolicies: EmailThreadLinkedPolicyDto[];
  participants: string[];
  policyId?: string;
  isOpen: boolean;
  onToggle: () => void;
  onSetArchived: (emailId: string, archived: boolean) => void;
}) {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const counterpart = counterpartOf(email);
  const linkProps = { threadId, email, threadPolicies, participantAddresses: participants };
  const isToAttachHere =
    policyId !== undefined &&
    !email.isArchived &&
    !email.linkedPolicies.some(({ id }) => id === policyId);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section || isVisible) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) setIsVisible(true);
    });
    observer.observe(section);
    return () => observer.disconnect();
  }, [isVisible]);

  return (
    <Stack asChild gap={isOpen ? 4 : 0}>
      <section
        ref={sectionRef}
        className={cn(
          'group shrink-0 scroll-mt-4 rounded-lg border transition-colors',
          isOpen ? 'px-5 py-4' : 'px-3 py-2',
          // Archived: the card recedes into the pane, the body stays readable
          email.isArchived ? 'border-transparent' : 'bg-card shadow-xs',
        )}
        data-email-id={email.id}
        data-open={isOpen}
        data-testid="general-inbox-reading-pane-email"
      >
        <header className="flex items-center gap-3">
          <button
            type="button"
            className="flex min-w-0 flex-1 items-center gap-3 rounded-md text-left outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-expanded={isOpen}
            onClick={onToggle}
          >
            <Avatar
              aria-hidden
              className={cn(
                'shrink-0',
                isOpen ? 'size-8' : 'size-6',
                email.isArchived && 'opacity-50',
              )}
            >
              <AvatarFallback
                className={cn(
                  'bg-sky-100 font-bold text-slate-700',
                  isOpen ? 'text-[11px]' : 'text-[9px]',
                )}
              >
                {initialsOf(counterpart.label)}
              </AvatarFallback>
            </Avatar>
            <span className={cn('min-w-0 flex-1', !isOpen && 'flex items-baseline gap-2')}>
              <Text
                size="sm"
                truncate
                inline={!isOpen}
                weight={email.isArchived ? 'medium' : 'semibold'}
                tone={email.isArchived ? 'muted' : undefined}
                className={cn(!isOpen && 'shrink-0')}
              >
                {counterpart.label}
                {email.isArchived && (
                  <span className="sr-only"> · {t('generalInbox.archived')}</span>
                )}
              </Text>
              {isOpen
                ? email.subject !== threadSubject && (
                    <Text truncate size="xs" tone="muted">
                      {email.subject || t('generalInbox.noSubject')}
                    </Text>
                  )
                : email.preview && (
                    <Text inline truncate size="xs" tone="muted" className="min-w-0">
                      {email.preview}
                    </Text>
                  )}
            </span>
            {isToAttachHere && <ToAttachBadge />}
            <InboxDate
              className="shrink-0 text-xs tabular-nums text-muted-foreground"
              date={email.date}
            />
          </button>
          {isOpen && (
            <ArchiveButton
              scope="email"
              isArchived={email.isArchived}
              onClick={() => onSetArchived(email.id, !email.isArchived)}
            />
          )}
        </header>
        {isOpen && (
          <>
            <EmailPolicyLinks {...linkProps} />
            {isVisible ? <EmailDetail emailId={email.id} /> : <Skeleton className="h-64 w-full" />}
          </>
        )}
      </section>
    </Stack>
  );
}

const HIGHLIGHT_ON = {
  boxShadow: '0 0 0 2px color-mix(in oklab, var(--color-primary) 55%, transparent)',
  backgroundColor: 'color-mix(in oklab, var(--color-primary) 6%, var(--color-card))',
};
const HIGHLIGHT_KEYFRAMES: Keyframe[] = [
  HIGHLIGHT_ON,
  { ...HIGHLIGHT_ON, offset: 0.5 },
  { boxShadow: '0 0 0 2px transparent', backgroundColor: 'var(--color-card)' },
];

const spansSeveralMonths = (emails: EmailThreadEmailDto[]) => {
  const [first] = emails;
  const last = emails.at(-1);
  if (!first || !last) return false;
  const start = new Date(first.date);
  const end = new Date(last.date);
  return start.getFullYear() !== end.getFullYear() || start.getMonth() !== end.getMonth();
};

export function ThreadReadingPane({
  threadId,
  emailId,
  emailSelectionCount,
  policyId,
  actions,
  onRead,
  onSetArchived,
}: {
  threadId: string;
  emailId: string | null;
  emailSelectionCount: number;
  // Shown from a policy's page: links read relative to that policy
  policyId?: string;
  actions?: ReactNode;
  // Fires once the thread is loaded, which marked it read server-side
  onRead: (threadId: string) => void;
  onSetArchived: (emailId: string, archived: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const scrollRef = useRef<HTMLDivElement>(null);
  // Ids whose default (only the latest and the targeted email open) the broker flipped
  const [toggledEmailIds, setToggledEmailIds] = useState<ReadonlySet<string>>(new Set());
  // Analysis relinks the thread's other emails after a manual change
  const { data, isSuccess, isPending, isError } = useQuery({
    ...emailThreadControllerGetThreadQueryOptions(threadId),
    refetchInterval: query => (query.state.data?.analysisPending ? 1000 : false),
  });
  const latestEmailId = data?.emails.at(-1)?.id;

  useEffect(() => {
    if (isSuccess) onRead(threadId);
  }, [isSuccess, threadId, onRead]);

  // A targeted email opens even if the broker folded it earlier
  useEffect(() => {
    if (!emailId) return;
    setToggledEmailIds(previous => {
      if (!previous.has(emailId)) return previous;
      const next = new Set(previous);
      next.delete(emailId);
      return next;
    });
  }, [emailId, emailSelectionCount]);

  useEffect(() => {
    const scroller = scrollRef.current;
    const targetId = emailId ?? latestEmailId;
    if (!scroller || !isSuccess || !targetId) return;
    const target = scroller.querySelector<HTMLElement>(`[data-email-id="${CSS.escape(targetId)}"]`);
    // Not scrollIntoView: it would also scroll the page around the pane
    const pin = () => {
      scroller.scrollTop = target
        ? target.offsetTop - Number.parseFloat(getComputedStyle(target).scrollMarginTop)
        : 0;
    };
    pin();
    if (emailId) {
      target?.animate(HIGHLIGHT_KEYFRAMES, {
        duration: 1600,
        easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
      });
    }
    // Bodies load after the first scroll: keep the target pinned until the user scrolls
    const observer = new ResizeObserver(pin);
    for (const section of Array.from(scroller.children)) observer.observe(section);
    const release = () => observer.disconnect();
    const userScrollEvents = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const;
    for (const type of userScrollEvents) scroller.addEventListener(type, release, { once: true });
    return () => {
      release();
      for (const type of userScrollEvents) scroller.removeEventListener(type, release);
    };
  }, [isSuccess, threadId, emailId, emailSelectionCount, latestEmailId]);

  const subject = data?.emails.at(-1)?.subject ?? '';
  const threadPolicies = uniqBy(
    data?.emails.flatMap(({ linkedPolicies }) => linkedPolicies) ?? [],
    ({ id }) => id,
  );
  const toggleEmail = (id: string) =>
    setToggledEmailIds(previous => {
      const next = new Set(previous);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  return (
    <Stack gap={0} className="min-h-0 min-w-0" data-testid="general-inbox-reading-pane">
      {isError ? (
        <Text size="sm" tone="destructive" className="p-6">
          {t('generalInbox.threadError')}
        </Text>
      ) : isPending ? (
        <Stack gap={3} className="p-6">
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-40 w-full" />
        </Stack>
      ) : (
        <>
          <header className="flex shrink-0 items-start gap-3 border-b bg-card px-6 py-3.5">
            <div className="min-w-0 flex-1">
              <Title order={2} truncate size="md" title={subject}>
                {subject || t('generalInbox.noSubject')}
              </Title>
              <Text truncate size="xs" tone="muted" className="mt-0.5">
                {t('generalInbox.emailCount', { count: data.emails.length })} ·{' '}
                {data.account.primaryEmailAddress}
                {data.emails[0] &&
                  spansSeveralMonths(data.emails) &&
                  ` · ${t('generalInbox.startedOn', {
                    date: formatDate(new Date(data.emails[0].date), i18n.language),
                  })}`}
              </Text>
            </div>
            {actions}
          </header>
          <Stack
            gap={2}
            ref={scrollRef}
            className="relative min-h-0 flex-1 overflow-y-auto bg-muted/40 p-4"
            data-testid="general-inbox-reading-pane-scroll"
          >
            {data.emails.map(email => (
              <ThreadEmailSection
                key={email.id}
                threadId={threadId}
                email={email}
                threadSubject={subject}
                threadPolicies={threadPolicies}
                participants={participantAddresses(data.emails, data.account.primaryEmailAddress)}
                {...(policyId && { policyId })}
                isOpen={
                  (email.id === latestEmailId || email.id === emailId) !==
                  toggledEmailIds.has(email.id)
                }
                onToggle={() => toggleEmail(email.id)}
                onSetArchived={onSetArchived}
              />
            ))}
          </Stack>
        </>
      )}
    </Stack>
  );
}
